import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import AOS from 'aos';

/**
 * Owns the single AOS instance for the whole app.
 *
 * AOS has no teardown API. `AOS.init()` installs a `MutationObserver` on
 * `document.body` plus scroll and resize listeners that outlive whatever
 * component called it. Twelve route components each called `init()` from their
 * own `ngAfterViewInit`, so every visit to one of those pages left another
 * observer running against the live document. Landing afterwards on a large
 * page that keeps mutating — About Us, which runs a 2s pillar carousel and a
 * 4s gallery carousel — made those observers re-measure every `[data-aos]`
 * element on every mutation until the main thread locked and the whole page,
 * footer included, stopped responding. That was BUG_001.
 *
 * So: initialise once, with the mutation observer switched off, and re-measure
 * explicitly instead. Two things trigger a re-measure:
 *
 *  - `AppComponent` calls `refresh()` on every completed navigation;
 *  - `AosTargetDirective` calls `refresh()` whenever Angular creates a
 *    `[data-aos]` element, which covers the listing pages where filtering
 *    rebuilds the whole `*ngFor`.
 *
 * Components need no AOS code of their own beyond importing the directive.
 */
@Injectable({ providedIn: 'root' })
export class AosService {
  private started = false;
  private pendingRefresh: ReturnType<typeof setTimeout> | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  /** Called once, from `AppComponent.ngOnInit`. Safe to call again; it no-ops. */
  init(): void {
    if (!isPlatformBrowser(this.platformId) || this.started) {
      return;
    }

    // Everything else stays on AOS's defaults, so the animations look exactly
    // as they did when each component called a bare `AOS.init()`.
    AOS.init({ disableMutationObserver: true });
    this.started = true;
  }

  /**
   * Ask AOS to re-collect its targets from the document.
   *
   * `refreshHard()` rebuilds the element list; plain `refresh()` would only
   * recalculate offsets for elements AOS already knew about — on a route
   * change or a filter change those are the previous, now-detached nodes.
   *
   * Debounced, because the usual caller is one directive instance per
   * `[data-aos]` element: filtering the case studies grid creates 22 of them
   * at once and they must collapse into a single refresh.
   */
  refresh(): void {
    if (!isPlatformBrowser(this.platformId) || !this.started) {
      return;
    }

    if (this.pendingRefresh !== null) {
      clearTimeout(this.pendingRefresh);
    }

    this.pendingRefresh = setTimeout(() => {
      this.pendingRefresh = null;
      AOS.refreshHard();
    }, 50);
  }
}
